/**
 * ProductsPage.tsx — the Products tab (story E0-BO-NUM6; wireframe states 7-10,
 * `docs/superpowers/specs/2026-08-06-backoffice-numbers-panel-wireframes.md`).
 *
 * Four pieces, each its own UI mechanism per the wireframe's explicit "hard
 * bar" (N3 AC4 — never let two different facts look the same):
 *   1. `ProductCatalogueTable` (state 7) — every product, `is_active` shown as
 *      a badge AND the whole row muted when inactive (dual signal).
 *   2. `TenantAvailabilityList` (state 8) — a SEPARATE list, not a filtered/
 *      highlighted view of the catalogue table, of only the products
 *      currently available to this tenant (membership, not a status column);
 *      an explicit empty state, never an error (N3 AC3).
 *   3. `AvailabilityToggleGrid` (state 9) — every catalogue product, toggle
 *      reflects this tenant's `tenant_products.is_active`. Enabling is a
 *      single-step optimistic write (no in-flight-conversation risk).
 *      Disabling opens the destructive-style red confirm panel first (same
 *      shell as the Number tab's clear-confirm) — the toggle itself never
 *      fires the write. A globally-inactive product's toggle is disabled
 *      with an inline note. No delete affordance anywhere in this file.
 *   4. `NewProductForm` (state 10) — create-only; retiring is the catalogue
 *      row's own control (N5 AC4), never a control on this form.
 *
 * Retire (N5 AC4): a "Retire" button on each ACTIVE catalogue row flips
 * `Product.is_active` to false via the existing `retireProduct()` client
 * method (POST .../retire — never a delete). It is platform-wide and
 * arguably higher-stakes than the per-tenant availability toggle, so it
 * gets the same destructive-style red confirm panel before the write fires
 * (never on the button click alone). Already-inactive products show no
 * retire control — nothing to retire.
 *
 * `TenantProductAvailability` rows from the hook include soft-disabled
 * entries (`is_active: false` stays in the table) — (2) filters to
 * `is_active`, while (3) reads the raw list to know each row's current
 * on/off state, including rows the tenant has toggled off before.
 */

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiError, UnauthorizedError, type ApiClient } from '../../api/client';
import type { ProductConfig, TenantProductAvailability } from '../../api/types';
import { UnauthorizedScreen } from '../../auth/UnauthorizedScreen';
import { NewProductForm } from './NewProductForm';
import { useProductsQuery } from './useProductsQuery';
import { Badge } from '../../components/Badge';
import { Button } from '../../components/Button';
import { DataTable, type DataTableColumn } from '../../components/DataTable';
import { EmptyState } from '../../components/EmptyState';
import { Notice } from '../../components/Notice';
import { Skeleton } from '../../components/Skeleton';
import floating from '../../lib/floating';

export interface ProductsPageProps {
  apiClient: ApiClient;
}

type ToggleWriteError = { productCode: string; kind: 'notFound' | 'unavailable' | 'unauthorized' };
const classifyToggleError = (error: unknown): 'notFound' | 'unavailable' | 'unauthorized' => {
  if (error instanceof UnauthorizedError) return 'unauthorized';
  if (error instanceof ApiError && error.status === 404) return 'notFound';
  return 'unavailable';
};
const ProductStatusBadge = ({ isActive }: { isActive: boolean }) => {
  const { t } = useTranslation();
  return isActive ? (
    <Badge tone="ok" data-testid="products-status-active">
      {t('products.catalogue.active')}
    </Badge>
  ) : (
    <Badge tone="neutral" hollow className="text-ink-3" data-testid="products-status-inactive">
      {t('products.catalogue.inactive')}
    </Badge>
  );
};
/** Destructive-style confirm (red, not gold) — same shell as the toggle grid's disable confirm. */
const RetireConfirm = ({
  product,
  onCancel,
  onConfirm,
  busy,
}: {
  product: ProductConfig;
  onCancel: () => void;
  onConfirm: () => void;
  busy: boolean;
}) => {
  const { t } = useTranslation();
  return (
    <div
      role="group"
      aria-label={t('products.catalogue.retireConfirmHeading')}
      data-testid="products-retire-confirm"
      className="flex flex-col gap-2 rounded-lg bg-surface-1 p-3 ring-2 ring-inset ring-status-down"
    >
      <p className="text-meta text-ink-1">
        {t('products.catalogue.retireConfirmBody', { product: product.display_name })}
      </p>
      <div className="flex gap-3">
        <button
          type="button"
          data-testid="products-retire-confirm-submit"
          disabled={busy}
          aria-busy={busy}
          onClick={onConfirm}
          className="inline-flex items-center rounded-lg bg-status-down px-3 py-1 text-meta font-medium text-canvas hover:bg-status-down/90 disabled:opacity-60"
        >
          {busy ? t('products.catalogue.retiring') : t('products.catalogue.retireConfirm')}
        </button>
        <button
          type="button"
          data-testid="products-retire-cancel"
          disabled={busy}
          onClick={onCancel}
          className="inline-flex items-center rounded-lg bg-surface-2 px-3 py-1 text-meta font-medium text-ink-1 ring-1 ring-inset ring-ink-3/20 hover:bg-surface-3 disabled:opacity-60"
        >
          {t('products.catalogue.retireCancel')}
        </button>
      </div>
    </div>
  );
};

// --- State 7: the catalogue table --------------------------------------------

const ProductCatalogueTable = ({
  products,
  retireBusy,
  pendingRetire,
  retireError,
  onRequestRetire,
  onCancelRetire,
  onConfirmRetire,
}: {
  products: ProductConfig[];
  retireBusy: string | null;
  pendingRetire: ProductConfig | null;
  retireError: ToggleWriteError | null;
  onRequestRetire: (product: ProductConfig) => void;
  onCancelRetire: () => void;
  onConfirmRetire: () => void;
}) => {
  const { t } = useTranslation();
  if (products.length === 0) {
    return (
      <EmptyState data-testid="products-catalogue-empty" title={t('products.catalogue.empty')} />
    );
  }
  const columns: DataTableColumn<ProductConfig>[] = [
    {
      key: 'code',
      header: t('products.catalogue.columns.code'),
      renderCell: (p) => p.product_code,
    },
    {
      key: 'displayName',
      header: t('products.catalogue.columns.displayName'),
      renderCell: (p) => p.display_name,
    },
    {
      key: 'collateralModel',
      header: t('products.catalogue.columns.collateralModel'),
      renderCell: (p) => t(`products.collateralModel.${p.collateral_model}`),
    },
    {
      key: 'pricingModel',
      header: t('products.catalogue.columns.pricingModel'),
      renderCell: (p) => t(`products.pricingModel.${p.pricing_model}`),
    },
    {
      key: 'status',
      header: t('products.catalogue.columns.status'),
      renderCell: (p) => <ProductStatusBadge isActive={p.is_active} />,
    },
    {
      key: 'actions',
      header: t('products.catalogue.columns.actions'),
      renderCell: (product) => {
        if (!product.is_active) return null;
        const busy = retireBusy === product.product_code;
        return (
          <div className="flex flex-col gap-2">
            <Button
              size="sm"
              variant="danger"
              className="min-h-11"
              data-testid={`products-retire-${product.product_code}`}
              disabled={busy}
              aria-busy={busy}
              onClick={() => onRequestRetire(product)}
            >
              {t('products.catalogue.retire')}
            </Button>
            {retireError != null && retireError.productCode === product.product_code ? (
              <p
                role="alert"
                data-testid={`products-retire-error-${product.product_code}`}
                className="text-meta text-status-down"
              >
                {t(`products.catalogue.retireErrors.${retireError.kind}`)}
              </p>
            ) : null}
            {pendingRetire?.product_code === product.product_code ? (
              <RetireConfirm
                product={pendingRetire}
                busy={busy}
                onCancel={onCancelRetire}
                onConfirm={onConfirmRetire}
              />
            ) : null}
          </div>
        );
      },
    },
  ];
  return (
    <DataTable
      columns={columns}
      rows={products}
      rowKey={(p) => p.product_code}
      caption={t('products.catalogue.tableLabel')}
      data-testid="products-catalogue-table"
      rowTestId="products-catalogue-row"
      rowProps={(p) => ({
        'data-product-code': p.product_code,
        'data-active': p.is_active ? 'true' : 'false',
        className: p.is_active ? 'text-ink-1' : 'text-ink-3',
      })}
    />
  );
};
// --- State 8: the tenant availability list (a SEPARATE list, not a filter of the table) ----

const TenantAvailabilityList = ({
  products,
  availableList,
}: {
  products: ProductConfig[];
  availableList: TenantProductAvailability[];
}) => {
  const { t } = useTranslation();
  const displayNameByCode = useMemo(
    () => new Map(products.map((p) => [p.product_code, p.display_name])),
    [products],
  );

  return (
    <section aria-labelledby="products-availability-heading" className="flex flex-col gap-2">
      <h2 id="products-availability-heading" className="text-h3 text-ink-1">
        {t('products.availability.heading')}
      </h2>
      {availableList.length === 0 ? (
        <div
          className="rounded-xl bg-surface-2 p-4 text-body text-ink-2"
          data-testid="products-availability-empty"
        >
          <p>{t('products.availability.empty')}</p>
          <p className="text-meta text-ink-3">{t('products.availability.emptyNotAnError')}</p>
        </div>
      ) : (
        <ul data-testid="products-availability-list" className="flex flex-col gap-1">
          {availableList.map((row) => (
            <li
              key={row.product_code}
              data-testid="products-availability-row"
              data-product-code={row.product_code}
              className="flex items-center gap-2 rounded-lg bg-surface-2 px-3 py-2 text-body text-ink-1"
            >
              <span aria-hidden="true" className="text-status-ok">
                ✓
              </span>
              <span>{row.product_code}</span>
              <span className="text-ink-2">{displayNameByCode.get(row.product_code) ?? '—'}</span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
};
/** Destructive-style confirm (red, not gold) — same shell the Number tab's clear-number confirm uses. */
const DisableConfirm = ({
  product,
  onCancel,
  onConfirm,
  busy,
}: {
  product: ProductConfig;
  onCancel: () => void;
  onConfirm: () => void;
  busy: boolean;
}) => {
  const { t } = useTranslation();
  return (
    <div
      role="group"
      aria-label={t('products.toggleGrid.disableConfirmHeading')}
      data-testid="products-disable-confirm"
      className="flex flex-col gap-3 rounded-lg bg-surface-1 p-3 ring-2 ring-inset ring-status-down"
    >
      <p className="text-body text-ink-1">
        {t('products.toggleGrid.disableConfirmBody', { product: product.display_name })}
      </p>
      <div className="flex gap-3">
        <button
          type="button"
          data-testid="products-disable-confirm-submit"
          disabled={busy}
          aria-busy={busy}
          onClick={onConfirm}
          className="inline-flex items-center rounded-lg bg-status-down px-4 py-1.5 text-body font-medium text-canvas hover:bg-status-down/90 disabled:opacity-60"
        >
          {busy ? t('products.toggleGrid.disabling') : t('products.toggleGrid.disableConfirm')}
        </button>
        <button
          type="button"
          data-testid="products-disable-cancel"
          disabled={busy}
          onClick={onCancel}
          className="inline-flex items-center rounded-lg bg-surface-2 px-4 py-1.5 text-body font-medium text-ink-1 ring-1 ring-inset ring-ink-3/20 hover:bg-surface-3 disabled:opacity-60"
        >
          {t('products.toggleGrid.disableCancel')}
        </button>
      </div>
    </div>
  );
};

// --- State 9: the availability toggle grid -----------------------------------

const AvailabilityToggleGrid = ({
  catalogue,
  availabilityByCode,
  rowBusy,
  pendingDisable,
  toggleError,
  onEnable,
  onRequestDisable,
  onCancelDisable,
  onConfirmDisable,
}: {
  catalogue: ProductConfig[];
  availabilityByCode: Map<string, TenantProductAvailability>;
  rowBusy: string | null;
  pendingDisable: ProductConfig | null;
  toggleError: ToggleWriteError | null;
  onEnable: (product: ProductConfig) => void;
  onRequestDisable: (product: ProductConfig) => void;
  onCancelDisable: () => void;
  onConfirmDisable: () => void;
}) => {
  const { t } = useTranslation();

  return (
    <section aria-labelledby="products-toggle-heading" className="flex flex-col gap-2">
      <h2 id="products-toggle-heading" className="text-h3 text-ink-1">
        {t('products.toggleGrid.heading')}
      </h2>
      <div
        data-testid="products-toggle-grid"
        className="flex flex-col divide-y divide-outline rounded-xl bg-surface-2 ring-1 ring-inset ring-ink-3/10"
      >
        {catalogue.map((product) => {
          const isAvailable = availabilityByCode.get(product.product_code)?.is_active ?? false;
          const disabledGlobally = !product.is_active;
          const busy = rowBusy === product.product_code;
          return (
            <div
              key={product.product_code}
              data-testid="products-toggle-row"
              data-product-code={product.product_code}
              className="flex flex-col gap-1 px-4 py-2.5"
            >
              <div className="flex items-center justify-between gap-3">
                <span className="text-body text-ink-1">
                  {product.product_code} — {product.display_name}
                </span>
                <button
                  type="button"
                  role="switch"
                  aria-checked={isAvailable}
                  aria-label={t('products.toggleGrid.toggleAria', {
                    product: product.display_name,
                  })}
                  data-testid={`products-toggle-${product.product_code}`}
                  disabled={disabledGlobally || busy}
                  aria-busy={busy}
                  onClick={() => (isAvailable ? onRequestDisable(product) : onEnable(product))}
                  className={`inline-flex h-6 w-11 items-center rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-outline-focus disabled:opacity-50 ${
                    isAvailable ? 'bg-status-ok' : 'bg-control'
                  }`}
                >
                  <span
                    aria-hidden="true"
                    className={`h-5 w-5 rounded-full bg-canvas transition-transform ${
                      isAvailable ? 'translate-x-5' : 'translate-x-0.5'
                    }`}
                  />
                </button>
              </div>
              {disabledGlobally ? (
                <p
                  data-testid={`products-toggle-note-${product.product_code}`}
                  className="text-meta text-ink-3"
                >
                  {t('products.toggleGrid.globallyInactiveNote')}
                </p>
              ) : null}
              {toggleError != null && toggleError.productCode === product.product_code ? (
                <p
                  role="alert"
                  data-testid={`products-toggle-error-${product.product_code}`}
                  className="text-meta text-status-down"
                >
                  {t(`products.toggleGrid.errors.${toggleError.kind}`)}
                </p>
              ) : null}
              {pendingDisable?.product_code === product.product_code ? (
                <DisableConfirm
                  product={pendingDisable}
                  onCancel={onCancelDisable}
                  onConfirm={onConfirmDisable}
                  busy={busy}
                />
              ) : null}
            </div>
          );
        })}
      </div>
    </section>
  );
};

export const ProductsPage = ({ apiClient }: ProductsPageProps) => {
  const { t } = useTranslation();
  const { catalogue, availability, phase, reload } = useProductsQuery(apiClient);

  const [showNewProductForm, setShowNewProductForm] = useState(false);
  const [pendingDisable, setPendingDisable] = useState<ProductConfig | null>(null);
  const [rowBusy, setRowBusy] = useState<string | null>(null);
  const [toggleError, setToggleError] = useState<ToggleWriteError | null>(null);
  const [pendingRetire, setPendingRetire] = useState<ProductConfig | null>(null);
  const [retireBusy, setRetireBusy] = useState<string | null>(null);
  const [retireError, setRetireError] = useState<ToggleWriteError | null>(null);

  const availabilityByCode = useMemo(
    () => new Map(availability.map((row) => [row.product_code, row])),
    [availability],
  );
  const availableList = useMemo(() => availability.filter((row) => row.is_active), [availability]);

  if (phase === 'unauthorized') {
    return <UnauthorizedScreen />;
  }

  const handleEnable = async (product: ProductConfig) => {
    setToggleError(null);
    setRowBusy(product.product_code);
    try {
      await apiClient.enableTenantProduct(product.product_code);
      await reload();
    } catch (error) {
      setToggleError({ productCode: product.product_code, kind: classifyToggleError(error) });
    } finally {
      setRowBusy(null);
    }
  };

  const handleDisableConfirm = async () => {
    if (pendingDisable == null) return;
    const product = pendingDisable;
    setToggleError(null);
    setRowBusy(product.product_code);
    try {
      await apiClient.disableTenantProduct(product.product_code);
      setPendingDisable(null);
      await reload();
    } catch (error) {
      setToggleError({ productCode: product.product_code, kind: classifyToggleError(error) });
      setPendingDisable(null);
    } finally {
      setRowBusy(null);
    }
  };

  const handleRetireConfirm = async () => {
    if (pendingRetire == null) return;
    const product = pendingRetire;
    setRetireError(null);
    setRetireBusy(product.product_code);
    try {
      await apiClient.retireProduct(product.product_code);
      setPendingRetire(null);
      await reload();
    } catch (error) {
      setRetireError({ productCode: product.product_code, kind: classifyToggleError(error) });
      setPendingRetire(null);
    } finally {
      setRetireBusy(null);
    }
  };

  const renderBranch1 = () => {
    if (phase === 'loading')
      return <Skeleton data-testid="products-loading" label={t('products.loading')} />;
    if (phase === 'error')
      return (
        <Notice
          tone="danger"
          data-testid="products-error"
          action={
            <Button data-testid="products-retry" onClick={() => floating(reload())}>
              {t('products.retry')}
            </Button>
          }
        >
          {t('products.errorBody')}
        </Notice>
      );
    return (
      <>
        <ProductCatalogueTable
          products={catalogue}
          retireBusy={retireBusy}
          pendingRetire={pendingRetire}
          retireError={retireError}
          onRequestRetire={(product) => {
            setRetireError(null);
            setPendingRetire(product);
          }}
          onCancelRetire={() => setPendingRetire(null)}
          onConfirmRetire={() => floating(handleRetireConfirm())}
        />
        <TenantAvailabilityList products={catalogue} availableList={availableList} />
        <AvailabilityToggleGrid
          catalogue={catalogue}
          availabilityByCode={availabilityByCode}
          rowBusy={rowBusy}
          pendingDisable={pendingDisable}
          toggleError={toggleError}
          onEnable={(product) => floating(handleEnable(product))}
          onRequestDisable={(product) => {
            setToggleError(null);
            setPendingDisable(product);
          }}
          onCancelDisable={() => setPendingDisable(null)}
          onConfirmDisable={() => floating(handleDisableConfirm())}
        />
      </>
    );
  };

  return (
    <section aria-labelledby="products-heading" className="flex flex-col gap-6">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <h1 id="products-heading" className="text-h1 text-ink-1">
          {t('products.title')}
        </h1>
        {!showNewProductForm ? (
          <button
            type="button"
            data-testid="products-new-product-cta"
            onClick={() => setShowNewProductForm(true)}
            className="inline-flex min-h-11 items-center rounded-lg bg-gold px-4 py-1.5 text-body font-medium text-on-gold hover:bg-gold/90"
          >
            {t('products.newProduct.cta')}
          </button>
        ) : null}
      </header>

      {showNewProductForm ? (
        <NewProductForm
          apiClient={apiClient}
          onCancel={() => setShowNewProductForm(false)}
          onCreated={() => {
            setShowNewProductForm(false);
            floating(reload());
          }}
        />
      ) : null}

      {renderBranch1()}
    </section>
  );
};
